import { createAction } from 'redux-actions';
import moment from 'moment';

import * as types from '../../constants/Api';
import * as userDataTypes from '../../constants/UserData';
import { appErrorAction } from '../../actions/App';
import { encodeConsentText } from '../common';
import { getValueFromObjects, getRequestHeader, checkAllApiCallsStatus } from './common';
import Product from './product.js';
import { userDataChangeAction } from '../../actions/UserData.js';

class ConsentService extends Product {

  constructor() {
    super();
    this.apiCallsStatus = {}; // Tracks status of API calls
  }

  static GET_CONSENTS = 'v2/consents';
  static CONSENT_TYPES = 'v1/consent-types';
  static CONSENT_TEMPLATE = 'v1/consent-templates';
  static CONSENT_COLLECTION_CHANNEL = 'CalcNBuy';
  static CONSENT_REQUESTER = 'Consent requester';
  static CONSENT_APPROVER = 'Consent approver';
  static CONSENT_PREDEFINED_ANSWER = 'Accepted';
  static CONSENT_DATE_FORMAT = 'YYYY-MM-DDThh:mm:ss';
  static consentCreated = createAction(types.CONSENT_CREATED);
  static recievedAPIs = createAction(userDataTypes.USERDATA_CHANGE);

  /* This method used to update the consetns in user data
  * @param payload
  * @returns {function(*): *}
  */
  consentCreatedAction(payload) {
    return dispatch => dispatch(ConsentService.consentCreated(payload));
  }

  /**
  * This method used to update api calls status in user data
  * @param payload
  */
  recievedAPIsAction(payload) {
    return (dispatch, getState) => {
      const userDataNew = getState().userData;
      userDataNew.apiCallsStatus = checkAllApiCallsStatus(payload);
      dispatch(ConsentService.recievedAPIs(userDataNew));
    }
  }


  getConsentTypesNoArgsService() {
    return (dispatch, getState) => {
      const state = getState();
      const { section: { packageData } } = state;
      const { initialValues: { consentTypes, consentUsage, globalPartyKey } } = packageData;

      return dispatch(consentService.getConsentTypesService(consentTypes, consentUsage, globalPartyKey));
    }
  }

    /**
     * This method used to get the Consents and templates from api
     * @param consentTypes
     * @param consentUsage
     * @param globalPartyKey
     * @returns {function(*, *): *}
     */
    getConsentTypesService(consentTypes, consentUsage, globalPartyKey)
    {
      return (dispatch, getState) => {
        const state = getState();
        return dispatch(
          this.getConsentTypes(
            ConsentService.CONSENT_TYPES,
            consentTypes,
            consentUsage
          )
        )
          .then((data) => {
            const userDataNew = state.userData;
            data.globalPartyKey = globalPartyKey
            userDataNew.consents = data;
            dispatch(userDataChangeAction({
              fields: [
                {
                  field: "consents",
                  value: data,
                }
              ]
            }));
          })
          .catch((data) => {
            dispatch(appErrorAction(data));
            return Promise.reject(data);
          })
          .finally(() => {
            dispatch(this.recievedAPIsAction(this.apiCallsStatus))
          })
      };
    }

    /**
     * This method used to filter the tempalte name
     * @param consentTypes
     * @param templateId
     * @returns {*}
     */
    getTemplateName(consentTypes, templateId)
    {
      return consentTypes.filter((item) => {
        if (item.questionTemplate &&
          item.questionTemplate.templateId === templateId) {
          return (item.questionTemplate.name);
        }
        return null;
      });
    }

    /**
     * This method used to get templates ids
     * @param consentTypes
     * @returns {*}
     */
    getTemplateIds(consentTypes)
    {
      return (consentTypes).map((item) => {
        if (item.questionTemplate) {
          return (item.questionTemplate.templateId);
        }
        return null;
      });
    }

    /**
     * This method used to call the consents types service
     * @param service
     * @param consentType
     * @param consentUsage
     * @returns {function(*=): *}
     */
    getConsentTypes(service, consentType, consentUsage)
    {
      return (dispatch) => {
        const headers = getRequestHeader();
        const params = { consentType, consentUsage };
        const apiKey = 'getConsentTypes'
        this.apiCallsStatus[apiKey] = false;
        return this.callApiGet(service, params, dispatch, headers, false)
          .then(data => {
            this.apiCallsStatus[apiKey] = true;
            const templateIdArray = this.getTemplateIds(data.consentTypes);
            const templateId = templateIdArray.join(',');
            return this.getConsentTemplates(templateId, ConsentService.CONSENT_TEMPLATE, dispatch)
              .then((templatesData) => {
                const consents = {};
                const templates = [];
                templatesData.forEach((template) => {
                  const templateNew = template;
                  const templateNameObj = this.getTemplateName(data.consentTypes,
                    template.templateId);
                  templateNew.consentType = templateNameObj[0].consentType;
                  templateNew.consentCardinality =
                    templateNameObj[0].questionTemplate.consentCardinality;
                  if (templateNew.templateName instanceof Array) {
                    templateNew.templateName = template.templateName.pop();
                  }
                  if (templateNew.version instanceof Array) {
                    templateNew.version = template.version.pop();
                  }
                  templates.push(templateNew);
                });
                consents.templates = templates;
                consents.consentUsage = consentUsage;
                return consents;
              });
          });
      };
    }

    /**
     * This method used to call the consent templates service
     * @param templateId
     * @param service
     * @param dispatch
     * @returns {*}
     */
    getConsentTemplates(templateId, service, dispatch)
    {
      const params = { templateId };
      const headers = getRequestHeader();
      const apiKey = 'getConsentTemplates'
      this.apiCallsStatus[apiKey] = false;
      return this.callApiGet(service, params, dispatch, headers, false)
        .then(data => {
          this.apiCallsStatus[apiKey] = true;
          dispatch(this.recievedAPIsAction(this.apiCallsStatus))
          return data.templates;
        });
    }

    /**
     * This method used to call the consents service to check current consents are already exist
     * @param consentType
     * @param globalPartyKey
     * @param dispatch
     * @returns {*}
     */
    verifyConsents(consentType, globalPartyKey, dispatch)
    {
      const headers = getRequestHeader();
      const params = {
        'predefinedAnswer': ConsentService.CONSENT_PREDEFINED_ANSWER,
        consentType,
        'collectionChannel': ConsentService.CONSENT_COLLECTION_CHANNEL,
        'requesterGlobalPartyKey': globalPartyKey
      };
      return this.callApiGet(ConsentService.GET_CONSENTS, params, dispatch, headers, true)
        .then(data => {
          return data;
        }).catch((data) => {
          return Promise.resolve(data);
        });
    }

    /**
     * This method used to filter the existing consents from request and sends only new
     * @param existingConsentsData
     * @param selectedConsents
     * @param consents
     * @returns {[]}
     */
    getConsentsToAdd(existingConsentsData, selectedConsents, consents)
    {
      const singleConsents = consents.templates.filter(
        (consent) => consent.consentCardinality === 'Single').map((item) => {
        return (item.consentType).toLowerCase();
      });
      const existingConsents = (existingConsentsData.consentQuestions) ?
        (existingConsentsData.consentQuestions).map((item) => {
          if (item.consentType && singleConsents.includes((item.consentType).toLowerCase())) {
            return (item.consentType).toLowerCase();
          }
          return '';
        }) : null;
      let consentsToAdd = [];
      if (existingConsents && existingConsents != null) {
        selectedConsents.forEach((consentName) => {
          if (!existingConsents.includes((consentName).toLowerCase())) {
            consentsToAdd.push(consentName);
          }
        });
      } else {
        consentsToAdd = selectedConsents;
      }
      return consentsToAdd;
    }

    /**
     * This method used to get the customer global party key from response
     * @param data
     * @returns
     */
    getCustomerGlobalPartyKey(data)
    {
      return (data.partyRoleInAgreement).filter(
        (item) => {
          if (item.partyRoleInAgreementKind === ConsentService.CONSENT_APPROVER) {
            return item.globalPartyKey;
          }
          return null;
        });
    }

    /**
     * Update Customer Global party key in redux
     * @param data
     * @param state
     * @param dispatch
     * @returns {*}
     */
    updateCustomerGlobalPartyKey(data, state, dispatch)
    {
      const customerGlobalParyKey = this.getCustomerGlobalPartyKey(data);
      const userDataNew = state.userData;
      userDataNew.consents.customerGlobalParyKey =
        customerGlobalParyKey[0].globalPartyKey;
      return dispatch(this.consentCreatedAction(userDataNew));
    }
    /**
     * This mtethod used to update the consents.
     * @returns {function(*=, *): *}
     * @param params
     * @param formValues
     */
    updateConsentsService(params, formValues)
    {
      const selectedConsents = params.filter((param) => {
        if (getValueFromObjects(param.field, formValues) === true) {
          return param;
        }
        return null;
      }).map((item) => item.field);
      return (dispatch, getState) => {
        const state = getState();
        const { userData: { consents, PolicyNumber, policyNumber, basketList, id_token } } = state;
        const { section: { packageData } } = state;
        const existingInsurancecompany = getValueFromObjects('terminateExternalPartyCode-label', formValues);
        const { initialValues: { consentTypes, globalPartyKey } } = packageData;
        if (!id_token) {
          return Promise.resolve();
        }
        return this.verifyConsents(consentTypes, globalPartyKey, dispatch)
          .then(existingConsentsData => {
            const consentsToAdd = this.getConsentsToAdd(existingConsentsData,
              selectedConsents, consents);
            if (consentsToAdd && consentsToAdd.length > 0) {
              const headers = getRequestHeader();
              let newPolicyNumber = (PolicyNumber && PolicyNumber !== 'undefined') ?
                PolicyNumber : policyNumber;
              if (newPolicyNumber === null || newPolicyNumber === undefined) {
                newPolicyNumber = `${basketList[0]} - Køb fejlet`;
              }
              const consentRequestModel = this.preparePostConsentsRequest(consentsToAdd, consents,
                existingInsurancecompany, newPolicyNumber);
              return this.callApiPost(ConsentService.GET_CONSENTS,
                consentRequestModel, dispatch, headers, true)
                .then(data => {
                  return this.updateCustomerGlobalPartyKey(data, state, dispatch);
                }).catch((data) => {
                  return Promise.resolve(data);
                });
            }
            return this.updateCustomerGlobalPartyKey(existingConsentsData, state, dispatch);
          });
      };
    }

    /**
     * This method used to prepare the request model for consents service.
     * @param selectedConsents
     * @param consents
     * @param existingInsurancecompany
     * @param policyNumber
     * @returns
     */
    preparePostConsentsRequest(selectedConsents, consents, existingInsurancecompany, policyNumber)
    {
      const consentRequestModel = {
        'collectionChannel': ConsentService.CONSENT_COLLECTION_CHANNEL,
        'partyRoleInAgreement': this.getPartyRoleInAgreement(consents.globalPartyKey),
        'consents': [],
      };
      const consentQuestions = [];
      selectedConsents.forEach(consentName => {
        const consentTemplate = consents.templates.find(
          (consent) => (consent.templateName === consentName));
        if (!consentTemplate) {
          return;
        }
        consentQuestions.push(this.consentsRequestModel(consentTemplate, consents.consentUsage,
          existingInsurancecompany, policyNumber));
      });
      consentRequestModel.consents = consentQuestions;
      return consentRequestModel;
    }

    getRequestModel(civilRegistrationNumber)
    {
      const requestModel = {};
      requestModel.partyRoles = [
        {
          'partyRoleKind': 'CUSTOMER',
          'partyTypeCode': 'PERSON',
          'partyRoleName': {
            'partyName': 'TRYG',
            'role': 'INSURANCE_COMPANY',
          },
          'assignedIdentifier': {
            'identifierType': 'CPR',
            'identifierValue': civilRegistrationNumber.replace('-', ''),
          },
        },
      ];
      return requestModel;
    }

    /**
     * This method used to map the GPC with role player id
     * @returns {*}
     */
    putGPCService()
    {
      return (dispatch, getState) => {
        const state = getState();
        const { userData: { consents, civilRegistrationNumber, id_token } } = state;
        if (!id_token) {
          return Promise.resolve();
        }
        const service = `v1/party/${consents.customerGlobalParyKey}/roles/keys`;
        const headers = getRequestHeader();
        headers.SYSTEM_ROLE_KIND = 'MF_KUNDE';
        const requestModel = this.getRequestModel(civilRegistrationNumber);
        return this.callApiPut(service, requestModel, dispatch, headers, true)
          .then(data => {
            return data;
          }).catch((data) => {
            return Promise.resolve(data);
          });
      };
    }

    /**
     * This method used to build request model for consentQuestions
     * @param consent
     * @param consentUsage
     * @param existingInsurancecompany
     * @param policyNumber
     * @returns
     */
    consentsRequestModel(consent, consentUsage, existingInsurancecompany, policyNumber)
    {
      const consentData = {
        consentQuestion: {
          'consentType': consent.consentType,
          'consentUsage': consentUsage,
          'templateId': consent.templateId,
          'templateVersion': consent.version,
          'optional': false,
        },
        'consentAgreement': {
          'status': ConsentService.CONSENT_PREDEFINED_ANSWER,
          'consentCreated': moment(new Date()).format(ConsentService.CONSENT_DATE_FORMAT),
        },
        'document': this.getConsentDocument(consent),
      };
      if (consentData.consentQuestion.consentType === 'Terminate_Insurance') {
        consentData.document = this.getConsentDocument(consent,
          existingInsurancecompany);
      }
      if (consentData.consentQuestion.consentType === 'Payment_service') {
        consentData.insuranceAgreement = this.getInsuranceAgent(consent.consentType, policyNumber);
      }
      return consentData;
    }

    /**
     * This method used to get the partyRoleInAgreement request
     * @param globalPartyKey
     * @returns
     */
    getPartyRoleInAgreement(globalPartyKey)
    {
      return [
        {
          'partyTypeCode': 'Person',
          'partyRoleInAgreementKind': ConsentService.CONSENT_APPROVER,
        },
        {
          'partyTypeCode': 'Organization',
          'partyRoleInAgreementKind': ConsentService.CONSENT_REQUESTER,
          globalPartyKey,
        },
      ];
    }

    /**
     * This method used to build InsuranceAgent request
     * @param consentType
     * @param policyNumber
     * @returns {{}}
     */
    getInsuranceAgent(consentType, policyNumber = null)
    {
      const insuranceAgreement = {};
      if (consentType === 'Payment_service') {
        insuranceAgreement.identifier = policyNumber;
      }
      insuranceAgreement.creationDateTime = moment(new Date()).format(
        ConsentService.CONSENT_DATE_FORMAT);
      return insuranceAgreement;
    }

    /**
     * This method used to build document request
     * @param consent
     * @param existingInsurancecompany
     * @returns
     */
    getConsentDocument(consent, existingInsurancecompany = null)
    {
      let acceptedText = consent.content;
      if (existingInsurancecompany && existingInsurancecompany != null) {
        acceptedText = acceptedText.concat(existingInsurancecompany);
      }
      return {
        'typeName': 'template system',
        'format': 'json',
        'documentStatus': 'Not filled',
        'templateVersion': consent.version,
        'attachment': encodeConsentText(acceptedText),
      };
    }

    /**
     * This method used get date in the given format
     * @param date
     * @param dateFormat
     * @returns {string}
     */
    getExpiryDateFormat(date, dateFormat)
    {
      return moment(date).add(1, 'years').format(dateFormat);
    }
  }

  export
  const
  consentService = new ConsentService();
